import { useState, useEffect, useCallback, memo, type ClipboardEvent } from 'react';
import { Bug, X, Send, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { authFetch, API_BASE } from '../../services/api/client';
import { useLocation } from 'react-router-dom';

interface ConsoleError {
  message: string;
  timestamp: number;
}

interface BugReportButtonProps {
  propertyId: string;
}

interface BugReportAttachment {
  id: string;
  filename: string;
  contentType: string;
  dataBase64: string;
  previewUrl: string;
  sizeBytes: number;
}

const MAX_ATTACHMENTS = 4;
const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;
const SUPPORTED_IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp']);

async function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to read image data'));
      }
    };
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read image data'));
    reader.readAsDataURL(file);
  });
}

function buildAttachmentFilename(file: File, index: number): string {
  const ext = file.type === 'image/jpeg' ? 'jpg' : file.type === 'image/webp' ? 'webp' : 'png';
  const base = file.name?.trim() ? file.name.replace(/\.[^.]+$/, '') : `screenshot-${index + 1}`;
  const safeBase = base.replace(/[^a-zA-Z0-9._-]+/g, '-').replace(/^-+|-+$/g, '');
  return `${safeBase || `screenshot-${index + 1}`}.${ext}`;
}

// Capture console errors for context
const consoleErrors: ConsoleError[] = [];
const MAX_ERRORS = 10;

// Only set up error listener once
if (typeof window !== 'undefined' && !window.__bugReporterInitialized) {
  window.__bugReporterInitialized = true;

  const originalConsoleError = console.error;
  console.error = (...args) => {
    consoleErrors.push({
      message: args.map((a) => (typeof a === 'object' ? JSON.stringify(a) : String(a))).join(' '),
      timestamp: Date.now(),
    });
    // Keep only last N errors
    while (consoleErrors.length > MAX_ERRORS) {
      consoleErrors.shift();
    }
    originalConsoleError.apply(console, args);
  };

  // Also capture unhandled errors
  window.addEventListener('error', (event) => {
    consoleErrors.push({
      message: `${event.message} at ${event.filename}:${event.lineno}:${event.colno}`,
      timestamp: Date.now(),
    });
    while (consoleErrors.length > MAX_ERRORS) {
      consoleErrors.shift();
    }
  });

  // Capture unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    consoleErrors.push({
      message: `Unhandled Promise: ${event.reason}`,
      timestamp: Date.now(),
    });
    while (consoleErrors.length > MAX_ERRORS) {
      consoleErrors.shift();
    }
  });
}

// Extend Window interface
declare global {
  interface Window {
    __bugReporterInitialized?: boolean;
  }
}

export const BugReportButton = memo(function BugReportButton({ propertyId }: BugReportButtonProps) {
  const [showModal, setShowModal] = useState(false);
  const [description, setDescription] = useState('');
  const [attachments, setAttachments] = useState<BugReportAttachment[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<{
    success: boolean;
    message: string;
    issueUrl?: string;
  } | null>(null);

  const location = useLocation();

  const handleSubmit = useCallback(async () => {
    if (!description.trim() || description.trim().length < 10) {
      setSubmitResult({
        success: false,
        message: 'Please provide a description of at least 10 characters.',
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitResult(null);

    try {
      const response = await authFetch(`${API_BASE}/api/bug-report`, {
        method: 'POST',
        body: JSON.stringify({
          description: description.trim(),
          attachments: attachments.map((attachment) => ({
            filename: attachment.filename,
            content_type: attachment.contentType,
            data_base64: attachment.dataBase64,
          })),
          context: {
            route: location.pathname,
            property: propertyId,
            timestamp: new Date().toISOString(),
            console_errors: consoleErrors.map((e) => e.message),
            user_agent: navigator.userAgent,
          },
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(error.detail || `HTTP ${response.status}`);
      }

      const data = await response.json();
      setSubmitResult({
        success: true,
        message:
          typeof data.message === 'string'
            ? data.message
            : 'Bug report submitted! Auto-fix will be attempted.',
        issueUrl: data.issue_url,
      });

      // Clear form after success
      setDescription('');
      setAttachments([]);
      setAttachmentError(null);
    } catch (error) {
      console.error('Bug report submission failed:', error);
      setSubmitResult({
        success: false,
        message: error instanceof Error ? error.message : 'Failed to submit bug report',
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [attachments, description, location.pathname, propertyId]);

  const handleClose = useCallback(() => {
    setShowModal(false);
    setSubmitResult(null);
    // Don't clear description immediately in case they want to re-open
  }, []);

  const handleRemoveAttachment = useCallback((attachmentId: string) => {
    setAttachments((current) => current.filter((attachment) => attachment.id !== attachmentId));
    setAttachmentError(null);
  }, []);

  const handlePaste = useCallback(
    async (event: ClipboardEvent<HTMLTextAreaElement>) => {
      const imageItems = Array.from(event.clipboardData.items).filter(
        (item) => item.kind === 'file' && SUPPORTED_IMAGE_TYPES.has(item.type),
      );

      if (imageItems.length === 0) {
        return;
      }

      event.preventDefault();
      setAttachmentError(null);
      setSubmitResult(null);

      if (attachments.length + imageItems.length > MAX_ATTACHMENTS) {
        setAttachmentError(`You can attach up to ${MAX_ATTACHMENTS} screenshots.`);
        return;
      }

      try {
        const nextAttachments = await Promise.all(
          imageItems.map(async (item, index) => {
            const file = item.getAsFile();
            if (!file) {
              throw new Error('Clipboard image could not be read.');
            }
            if (file.size > MAX_ATTACHMENT_BYTES) {
              throw new Error(`${buildAttachmentFilename(file, index)} exceeds the 5 MB limit.`);
            }

            const dataUrl = await fileToDataUrl(file);
            const commaIndex = dataUrl.indexOf(',');
            if (commaIndex === -1) {
              throw new Error('Clipboard image data was malformed.');
            }

            return {
              id: `${Date.now()}-${index}-${file.size}`,
              filename: buildAttachmentFilename(file, attachments.length + index),
              contentType: file.type,
              dataBase64: dataUrl.slice(commaIndex + 1),
              previewUrl: dataUrl,
              sizeBytes: file.size,
            };
          }),
        );

        setAttachments((current) => [...current, ...nextAttachments]);
      } catch (error) {
        setAttachmentError(
          error instanceof Error ? error.message : 'Failed to attach pasted screenshot.',
        );
      }
    },
    [attachments],
  );

  // Keyboard shortcut: Ctrl/Cmd + Shift + B to open bug reporter
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'b') {
        e.preventDefault();
        setShowModal(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <>
      {/* Bug Report Button */}
      <button
        type="button"
        onClick={() => setShowModal(true)}
        className="inline-flex min-h-9 min-w-9 items-center justify-center rounded-lg bg-[var(--surface)] border border-[var(--border-color)] text-[var(--text-muted)] hover:text-[var(--accent)] hover:border-[var(--accent)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--accent)]"
        title="Report a bug (Ctrl+Shift+B)"
        aria-label="Report a bug"
      >
        <Bug className="w-4 h-4" />
      </button>

      {/* Bug Report Modal */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"
          onClick={handleClose}
        >
          <div
            className="bg-[var(--surface-raised)] rounded-xl p-6 w-full max-w-lg border border-[var(--border-color)] shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Bug className="w-5 h-5 text-[var(--accent)]" />
                <h2 className="text-lg font-semibold text-[var(--text-primary)]">Report a Bug</h2>
              </div>
              <button
                onClick={handleClose}
                className="text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Description */}
            <p className="text-sm text-[var(--text-muted)] mb-4">
              Describe what&apos;s broken. Context (current page, property, recent errors) will be
              captured automatically. You can also paste screenshots directly into the box.
            </p>

            {/* Result message */}
            {submitResult && (
              <div
                className={`mb-4 p-3 rounded-lg flex items-start gap-2 ${
                  submitResult.success
                    ? 'bg-[var(--success-bg)] border border-[var(--success)]/25'
                    : 'bg-[var(--error-bg)] border border-[var(--error-border)]'
                }`}
              >
                {submitResult.success ? (
                  <CheckCircle className="w-5 h-5 text-[var(--success)] shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-[var(--error)] shrink-0 mt-0.5" />
                )}
                <div className="flex-1">
                  <p
                    className={`text-sm ${submitResult.success ? 'text-[var(--success)]' : 'text-[var(--error)]'}`}
                  >
                    {submitResult.message}
                  </p>
                  {submitResult.issueUrl && (
                    <a
                      href={submitResult.issueUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-[var(--accent)] hover:underline mt-1 inline-block"
                    >
                      View issue on GitHub
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Text area */}
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onPaste={handlePaste}
              placeholder="What's broken? Be specific about what you expected vs what happened..."
              className="w-full h-32 px-3 py-2 rounded-lg bg-[var(--surface)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:border-[var(--accent)] resize-none"
              disabled={isSubmitting}
              autoFocus
            />
            <div className="mt-2 flex items-center justify-between gap-3 text-xs text-[var(--text-muted)]">
              <span>Paste screenshots with Ctrl/Cmd+V.</span>
              <span>
                {attachments.length}/{MAX_ATTACHMENTS} screenshots
              </span>
            </div>

            {attachmentError && (
              <div className="mt-3 rounded-lg border border-[var(--error-border)] bg-[var(--error-bg)] px-3 py-2 text-xs text-[var(--error)]">
                {attachmentError}
              </div>
            )}

            {attachments.length > 0 && (
              <div className="mt-3 grid grid-cols-2 gap-3">
                {attachments.map((attachment) => (
                  <div
                    key={attachment.id}
                    className="rounded-lg border border-[var(--border-subtle)] bg-[var(--surface)] p-2"
                  >
                    <div className="relative overflow-hidden rounded-md border border-[var(--border-subtle)] bg-black/20">
                      <img
                        src={attachment.previewUrl}
                        alt={attachment.filename}
                        className="h-28 w-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveAttachment(attachment.id)}
                        className="absolute right-2 top-2 rounded-full bg-black/70 p-1 text-white transition-colors hover:bg-black"
                        aria-label={`Remove ${attachment.filename}`}
                        disabled={isSubmitting}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-xs text-[var(--text-primary)]">
                          {attachment.filename}
                        </p>
                        <p className="text-[11px] text-[var(--text-muted)]">
                          {(attachment.sizeBytes / 1024).toFixed(0)} KB
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Context preview */}
            <div className="mt-3 p-3 rounded-lg bg-[var(--surface)] border border-[var(--border-subtle)]">
              <p className="text-xs text-[var(--text-muted)] mb-2">Captured context:</p>
              <div className="text-xs text-[var(--text-tertiary)] space-y-1 font-mono">
                <div>
                  Route: <span className="text-[var(--text-muted)]">{location.pathname}</span>
                </div>
                <div>
                  Property: <span className="text-[var(--text-muted)]">{propertyId}</span>
                </div>
                {attachments.length > 0 && (
                  <div>
                    Screenshots:{' '}
                    <span className="text-[var(--accent)]">{attachments.length}</span>
                  </div>
                )}
                {consoleErrors.length > 0 && (
                  <div>
                    Console errors:{' '}
                    <span className="text-[var(--warning)]">{consoleErrors.length}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={handleClose}
                className="px-4 py-2 rounded-lg text-sm font-medium text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || !description.trim()}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Submit Report
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
});
